import React from 'react';
import { Link } from 'react-router-dom';
import { formatCPF, formatPhone, formatDate } from '../../utils/formatters';
import { Edit, Trash2, CheckCircle, XCircle, Clock } from 'lucide-react';

const UserAvatar = ({ user }) =>
  user.photoUrl ? (
    <img
      src={user.photoUrl}
      alt={user.name}
      className="h-9 w-9 rounded-full object-cover flex-shrink-0"
    />
  ) : (
    <div className="h-9 w-9 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
      <span className="text-sm font-medium text-gray-600">
        {user.name?.charAt(0).toUpperCase()}
      </span>
    </div>
  );

// Compactas (só ícone) na tabela; com texto e largura cheia nos cartões.
const UserActions = ({ user, onDelete, onToggleStatus, labeled = false }) => {
  const isActive = user.status === 'active';
  const toggleLabel = isActive ? 'Desativar' : 'Ativar';
  const ToggleIcon = isActive ? XCircle : CheckCircle;
  const toggleColor = isActive
    ? 'text-red-600 hover:text-red-900'
    : 'text-green-600 hover:text-green-900';
  const cardButton =
    'flex-1 inline-flex items-center justify-center gap-1 rounded border border-gray-200 py-2 text-sm font-medium';

  return (
    <>
      <Link
        to={`/admin/usuarios/${user.id}`}
        className={labeled ? `${cardButton} text-primary-600` : 'text-primary-600 hover:text-primary-900'}
        title="Editar"
      >
        <Edit className="h-4 w-4" />
        {labeled && 'Editar'}
      </Link>
      <button
        onClick={() => onToggleStatus(user.id)}
        className={labeled ? `${cardButton} ${toggleColor}` : toggleColor}
        title={toggleLabel}
      >
        <ToggleIcon className="h-4 w-4" />
        {labeled && toggleLabel}
      </button>
      <button
        onClick={() => onDelete(user.id)}
        className={labeled ? `${cardButton} text-red-600` : 'text-red-600 hover:text-red-900'}
        title="Excluir"
      >
        <Trash2 className="h-4 w-4" />
        {labeled && 'Excluir'}
      </button>
    </>
  );
};

const UsersTable = ({ users, onDelete, onToggleStatus }) => {
  const getStatusBadge = (status) => {
    const badges = {
      active: {
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle,
        label: 'Ativo',
      },
      inactive: {
        color: 'bg-red-100 text-red-800',
        icon: XCircle,
        label: 'Inativo',
      },
      pending: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: Clock,
        label: 'Pendente',
      },
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    return role === 'admin' ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
        Admin
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Usuário
      </span>
    );
  };

  if (!users || users.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        Nenhum usuário encontrado.
      </div>
    );
  }

  return (
    <>
      {/* Celular: cartões. Na tabela, as ações ficavam na 7ª coluna, fora da
          tela (x≈1119px numa tela de 412px). */}
      <ul className="md:hidden divide-y divide-gray-200" data-testid="users-cards">
        {users.map((user) => (
          <li key={user.id} className="p-4 space-y-3" data-testid={`user-card-${user.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center space-x-3 min-w-0">
                <UserAvatar user={user} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{user.name}</div>
                  <div className="text-xs text-gray-500 truncate">{user.email}</div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                {getStatusBadge(user.status)}
                {getRoleBadge(user.role)}
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <dt className="text-gray-500">CPF</dt>
              <dd className="text-gray-900 text-right">{formatCPF(user.cpf) || '—'}</dd>
              <dt className="text-gray-500">Telefone</dt>
              <dd className="text-gray-900 text-right">{formatPhone(user.phone) || '—'}</dd>
              <dt className="text-gray-500">Cadastro</dt>
              <dd className="text-gray-900 text-right">{formatDate(user.createdAt)}</dd>
            </dl>

            <div className="flex gap-2">
              <UserActions
                user={user}
                onDelete={onDelete}
                onToggleStatus={onToggleStatus}
                labeled
              />
            </div>
          </li>
        ))}
      </ul>

      <div className="hidden md:block overflow-x-auto" data-testid="users-table">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nome
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                CPF
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Telefone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Função
              </th>
              <th className="sticky right-0 bg-gray-50 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="group hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <UserAvatar user={user} />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      <div className="text-xs text-gray-500">
                        Cadastrado em {formatDate(user.createdAt)}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {user.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCPF(user.cpf)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatPhone(user.phone)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(user.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getRoleBadge(user.role)}
                </td>
                {/* Coluna fixa: com o menu lateral, a tabela é mais larga que a área
                    disponível e as ações ficavam fora da tela mesmo no computador. */}
                <td className="sticky right-0 bg-white group-hover:bg-gray-50 px-6 py-4 whitespace-nowrap text-right text-sm font-medium shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">
                  <div className="flex items-center justify-end space-x-2">
                    <UserActions user={user} onDelete={onDelete} onToggleStatus={onToggleStatus} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default UsersTable;

